import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPagePermissions } from '../services/pagePermissionsApi';

const PATH_TO_PERMISSION = {
  '/': 'calendar',
  '/feature-flags': 'featureFlags',
  '/query-vault': 'queryVault',
  '/azure-variables': 'azureVariables',
  '/tenant-provisioning': 'tenantProvisioning',
  '/tenant-onboarding': 'tenantOnboarding',
  '/env-scanner': 'envScanner',
  '/deployments': 'deployments',
  '/inactive-users': 'inactiveUsers',
  '/health-check': 'healthCheck',
  '/url-registry': 'urlRegistry',
  '/avalara-licensing': 'avalaraLicensing',
  '/event-dashboard': 'eventDashboard',
  '/training-revert': 'trainingRevert',
  '/database-export': 'databaseExport',
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-600 dark:text-gray-400 text-lg">
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export const AdminOnlyRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-600 dark:text-gray-400 text-lg">
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const DEFAULT_PERMISSIONS = { calendar: true, featureFlags: true, queryVault: true, inactiveUsers: true, healthCheck: true, urlRegistry: true, avalaraLicensing: false, eventDashboard: false };

export const PermissionRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading, logout } = useAuth();
  const location = useLocation();
  const [permissions, setPermissions] = useState(null);
  const path = location.pathname;

  useEffect(() => {
    if (isAdmin || !isAuthenticated) {
      setPermissions(null);
      return;
    }
    getPagePermissions()
      .then(setPermissions)
      .catch((err) => {
        if (err.status === 401) {
          logout();
        } else {
          setPermissions(DEFAULT_PERMISSIONS);
        }
      });
  }, [isAdmin, isAuthenticated, path, logout]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-600 dark:text-gray-400 text-lg">
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isAdmin) {
    return children;
  }

  const permissionKey = PATH_TO_PERMISSION[path];
  if (!permissionKey) {
    return children;
  }

  if (permissions === null) {
    if (DEFAULT_PERMISSIONS[permissionKey]) return children;
    return <Navigate to="/" replace />;
  }

  if (permissions[permissionKey] !== true) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
